'use client';

import { useCallback } from 'react';

import Link from '@/components/ui/Link';
import Image from '@/components/ui/Image';
import { CalendarIcon, CaretLeftIcon, CaretRightIcon, EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { track } from '@/lib/telemetry';
import { useAppHeaderOverrides } from '@/contexts/AppHeaderContext';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { PeriodPicker } from '@/components/PeriodPicker';
import { ThemeToggle } from '@/components/ThemeToggle';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import { usePathname } from '@/lib/router';
import { cn } from '@/lib/utils';
import { ScopeSwitcher } from '@/components/spaces/ScopeSwitcher';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { SPACE_PAGE_LABELS, spaceHref, spacePageFromPath, spaceTelemetryTarget } from '@/lib/spaces/navigation';

interface AppHeaderProps {
  showBackButton?: boolean;
  onBack?: () => void;
  backLabel?: string;
}

// Breadcrumb labels per top-level route. Longest matching prefix wins so detail
// routes (e.g. /investments/123) still resolve to their section label.
const PAGE_LABELS: Array<[string, string]> = [
  ['/transactions', 'Transações'],
  ['/invoices', 'Faturas'],
  ['/faturas', 'Faturas'],
  ['/budget', 'Categorias'],
  ['/metas', 'Metas'],
  ['/recurring-payments', 'Recorrentes'],
  ['/investments', 'Investimentos'],
  ['/profile', 'Perfil'],
  ['/connections', 'Conexões'],
  ['/expenses', 'Despesas'],
  ['/spaces/new', 'Criar espaço'],
  ['/spaces-rules', 'Espaços e regras'],
  ['/', 'Início'],
];

function pageLabel(pathname: string): string {
  const match = PAGE_LABELS.find(([prefix]) =>
    prefix === '/' ? pathname === '/' : pathname.startsWith(prefix),
  );
  return match?.[1] ?? 'Início';
}

function SpaceBreadcrumb() {
  const { scope, space } = useSpaceScope();
  if (scope !== 'space') return null;
  return (
    <>
      {space ? <Link href={spaceHref(space.id)} className="cursor-pointer truncate font-mono text-text-secondary">{space.name}</Link>
        : <span className="text-text-secondary">Espaço</span>}
      <CaretRightIcon aria-hidden className="size-3 shrink-0 text-text-tertiary" />
    </>
  );
}

function PersonalMobileBrand() {
  const { enabled, scope } = useSpaceScope();
  if (enabled || scope !== 'personal') return null;
  return (
    <Link href="/" className="flex items-center gap-2 md:hidden">
      <Image src="/favicon.png" alt="Vértice" width={24} height={24} />
      <span className="text-base font-bold text-text-primary">Vértice</span>
    </Link>
  );
}

export function AppHeader({
  showBackButton = false,
  onBack,
  backLabel = 'Voltar',
}: AppHeaderProps) {
  const { hideValues, toggleHideValues } = usePrivacy();
  const pathname = usePathname();
  const { enabled: spacesEnabled, scope } = useSpaceScope();
  const spacePage = spacePageFromPath(pathname);

  // `target` is the route being backed out of — the destination isn't knowable
  // here, and repeated back-outs from a screen are the interesting signal.
  const handleBack = useCallback(() => {
    track('nav.clicked', { surface: 'header_back', target: spaceTelemetryTarget(pathname), scope });
    onBack?.();
  }, [onBack, pathname, scope]);

  return (
    <header
      data-testid="app-header"
      data-tokens="v2"
      className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-8 border-b border-border bg-surface-glass px-4 backdrop-blur-[10px] sm:px-8"
    >
      <div className={cn('flex min-w-0 flex-1 items-center', spacesEnabled ? 'gap-2 sm:gap-3.5' : 'gap-3.5')}>
      <div className="hidden min-w-0 items-center gap-3 md:flex">
        <SidebarTrigger className="mr-1 size-[34px] cursor-pointer rounded-[10px] border border-border bg-surface text-text-secondary shadow-[var(--shadow-1)] transition-colors hover:bg-surface-sunken hover:text-text-primary [&_svg]:size-[18px]" />
        {/* Breadcrumb — the redesign's "Vértice › {page}" wayfinder. */}
        <nav aria-label="breadcrumb" className="flex min-w-0 items-center gap-1.5 text-sm">
          {pathname === '/' ? (
            <span className="font-mono text-text-secondary">Vértice</span>
          ) : (
            <Link
              href="/"
              className="font-mono text-text-secondary transition-colors hover:text-text-primary"
            >
              Vértice
            </Link>
          )}
          <CaretRightIcon size={12} className="text-text-tertiary" />
          <SpaceBreadcrumb />
          <span aria-current="page" className="shrink-0 font-semibold text-text-primary">{scope === 'space' && spacePage ? SPACE_PAGE_LABELS[spacePage] : pageLabel(pathname)}</span>
        </nav>
      </div>
      {showBackButton && onBack && (
        <button
          type="button"
          onClick={handleBack}
          className="mr-2 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-border-subtle bg-surface px-2.5 text-xs font-black text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary active:scale-[0.98] sm:mr-3 sm:px-3"
          aria-label={backLabel}
        >
          <CaretLeftIcon size={15} weight="bold" />
          <span className="hidden sm:inline">{backLabel}</span>
        </button>
      )}
      <ScopeSwitcher compact />
      <PersonalMobileBrand />

      <div className={cn('ml-auto flex shrink-0 items-center sm:gap-4', spacesEnabled ? 'gap-2' : 'gap-3')}>
        <GlobalPeriodPicker />
        <ThemeToggle />
        <NotificationBell />
        <button
          type="button"
          onClick={toggleHideValues}
          className="relative cursor-pointer text-text-secondary transition-colors hover:text-brand-700"
          aria-label={hideValues ? 'Mostrar valores' : 'Ocultar valores'}
        >
          {hideValues ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
        </button>
      </div>
      </div>
    </header>
  );
}

/**
 * Global period picker bound to the URL-backed `usePeriodSelection` hook.
 * Mounting it here makes the date scope consistent across every page that
 * cares (Faturas, Transactions). Pages where a global date scope makes no
 * sense hide it by declaring `<AppHeaderConfig hidePeriodPicker />`
 * (AppHeaderContext) — the home (cycle-scoped widgets), /metas,
 * /recurring-payments, /investments, /profile and /budget. That declaration
 * is the single lane; there is no route list here.
 */
function GlobalPeriodPicker() {
  const { hidePeriodPicker } = useAppHeaderOverrides();
  const { value, onChange } = usePeriodSelection();
  const { enabled, scope, space } = useSpaceScope();
  const page = spacePageFromPath(usePathname());

  if (scope === 'space' && (!space || page === 'settings')) return null;
  // A space page that hides the picker carries its own (`SpacePageHeader`), the trip pill included.
  if (hidePeriodPicker) return null;
  if (scope === 'space' && space?.kind === 'trip') {
    return <div role="group" aria-label="Período: toda a viagem" className="flex h-9 items-center gap-1.5 rounded-xl border border-border-subtle bg-surface px-2 text-xs font-medium text-text-secondary sm:px-3">
      <CalendarIcon aria-hidden className="size-4 shrink-0" />
      <span className="hidden sm:inline">toda a viagem</span>
    </div>;
  }
  if (hidePeriodPicker) return null;

  return <PeriodPicker value={value} onChange={onChange} compact={enabled} />;
}
